import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import EmojiPicker from "emoji-picker-react";
import FirstComment from "./components/FirstComment";
import { convertHtmlToLinkedIn } from "./linkedinCopy";
import { splitEditorLines } from "../../utils/splitOwnText";
import ImageSection from "./components/ImageSection";
import ImageSuggestions from "./components/ImageSuggestions";
import ToolbarTooltipButton from "../../components/common/ToolbarTooltipButton";
import Button from "../../components/common/Button";
import CarouselSection from "./components/CarouselSection";
import DocumentMedia from "./components/DocumentMedia";
import { X } from "lucide-react";
import { isSafeImage } from "../../utils/brandApi";
import useLogos from "../../utils/useLogos";
import LogoPickerModal from "./components/LogoPickerModal";
import { useCreateFlow } from "../../flow/CreateFlowContext";
import VideoSection from "./components/VideoSection";
import undo from "../../assets/tips/undo.svg";
import redo from "../../assets/tips/redo.svg";
import smile from "../../assets/tips/smile.svg";
import info from "../../assets/posts/info.svg";
import eye from "../../assets/posts/eye.svg";
import copyIcon from "../../assets/posts/copy.svg";
import arrowRight from "../../assets/common/arrow-right.svg";
import arrowLeft from "../../assets/common/arrow-left.svg";
import draft from "../../assets/navbar/draft.svg";
import publishIcon from "../../assets/navbar/publish.svg";

const MAX_CHARACTERS = 2000;

export default function TipsSection({
  generatedContent,
  onTextChange,
  previewImages,
  onPreviewClick,
  showGeneratedImageUI,
  isInsightsLoading = false,
  onRegenerateClick,
  onDeleteImage,
  imageLocked,
  mobileViewStep,
  onBack,
  onConfirm,
  generatedImages = [],
  selectedImageIndex = 0,
  onSelectImage,
  onUnlockStyle,
  onRegenerateImage,
  generatedImagesCount = 0,
  maxGeneratedImages = 5,
  textConfirmed = false,
  onTextConfirm,
  postSegments = null,
  onSegmentsChange,
  selectedPostId = null,
  generatingImage = false,
  imageError = null,
  postSaved = false,
  isEditMode = false,
  onLogoApplied,
  contentType = "single",
  carouselSlides = [],
  onSlidesChange,
  onCarouselExportReady,
  documentFilename,
  documentUrl,
  documentFile,
  onDocumentSelect,
  onDocumentDelete,
  onDownload,
  userEmail,
  onOpenAccount,
  preLogoImage,
  onCarouselPreviewState,
  autoSplit = false,
  onVideoRemoved,
  onPickVideo,
}) {
  const { t } = useTranslation();
  const imageSectionRef = useRef(null);
  const editorRef = useRef(null);
  const isUserEditingRef = useRef(false);
  const savedSelectionRef = useRef(null);
  const applyBordersTimerRef = useRef(null);
  const segmentMarkersRef = useRef(null);

  useEffect(() => {
    // Reset and lock markers whenever new content loads
    segmentMarkersRef.current = postSegments;
  }, [generatedContent]); // ← depends on generatedContent, NOT postSegments

  const [tipsText, setTipsText] = useState("");
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const canIncludeFirstComment = tipsText?.trim().length > 0;
  const editorHasText = tipsText?.trim().length > 0;
  const [isSmileDropdownOpen, setIsSmileDropdownOpen] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [fontStyle, setFontStyle] = useState("Font Styles");
  const [imageUrl, setImageUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [imgCopied, setImgCopied] = useState(false);
  const [imageMode, setImageMode] = useState(null);
  const lastScrolledTokenRef = useRef(null);
  const pendingScrollTokenRef = useRef(null);
  const { carouselLogo, setCarouselLogo, carouselLogoRel, setCarouselLogoRel, mediaTypeConfirmed, videoUpload } = useCreateFlow();
  const tooltipText = t("insights.tooltipInsights");
  const activeGeneratedImg = generatedImages[selectedImageIndex] || null;
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [logoPickError, setLogoPickError] = useState("");

  const updateEditorContent = (html, pushToHistory = true) => {
    if (!editorRef.current) return;
    if (pushToHistory) {
      setHistory((prev) => [...prev.slice(-49), editorRef.current.innerHTML]);
      setRedoStack([]);
    }
    editorRef.current.innerHTML = html;
    setTipsText(html);
    onTextChange?.(html);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  // Desktop: while the media is showing, the text can't be edited. Going Back to the
  // media type cards shows the editor again.
  const hideTextForMedia = !isMobile && textConfirmed && mediaTypeConfirmed;

  const { logos: savedLogos } = useLogos(userEmail);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!showLogoPicker) return;
    const onEsc = (e) => { if (e.key === "Escape") setShowLogoPicker(false); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [showLogoPicker]);

  /*useEffect(() => {
    if (generatedContent) setTipsText(generatedContent);
  }, [generatedContent]);*/

  useEffect(() => {
    if (previewImages && previewImages.length > 0) {
      const first = previewImages[0];
      if (first instanceof Blob || first instanceof File) {
        const url = URL.createObjectURL(first);
        setImageUrl(url);
        return () => URL.revokeObjectURL(url);
      }
      if (typeof first === "string") {
        setImageUrl(first);
        return;
      }
    }
    setImageUrl(null);
  }, [previewImages]);

  useEffect(() => {
    if (!imageLocked) {
      setImageMode(null);
    }
  }, [imageLocked]);

  useEffect(() => {
    if (generatedContent) {
      const isAlreadyHtml = generatedContent.trim().startsWith("<");
      const htmlToSet = isAlreadyHtml
        ? generatedContent
        : generatedContent
            .split("\n")
            .map((line) => `<div>${line || "<br>"}</div>`)
            .join("");

      // Always set tipsText regardless of editorRef
      setTipsText(htmlToSet);
      onTextChange?.(htmlToSet);

      // Only manipulate DOM if editor is available
      if (editorRef.current && !isUserEditingRef.current) {
        editorRef.current.innerHTML = htmlToSet;
        applySegmentBorders(editorRef.current, postSegments);
        const hookText = editorRef.current?.children[0]?.innerText || "";
      }
    }
  }, [generatedContent]);

  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setRedoStack((r) => [editorRef.current.innerHTML, ...r]);
    setHistory((h) => h.slice(0, -1));
    updateEditorContent(prev, false);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[0];
    setHistory((h) => [...h, editorRef.current.innerHTML]);
    setRedoStack((r) => r.slice(1));
    updateEditorContent(next, false);
  };

  const handleLogoFromBase64 = (src) => {
    if (!isSafeImage(src)) return;
    setLogoPickError("");
    const img = new Image();
    // Storage URLs taint the canvas without this — which breaks the PDF
    // export. Requires CORS on the blob container.
    img.crossOrigin = "anonymous";
    img.onload = async () => {
      try { await img.decode(); } catch {}
      setCarouselLogo(img);
      setShowLogoPicker(false);
    };
    img.onerror = () => {
      console.error("Logo failed to load (CORS on the blob container?):", src);
      setLogoPickError(t("logoEditor.loadFailed"));
    };
    img.src = src;
  };

  const handleLogoUploadRequest = () => setShowLogoPicker(true);

  const handleCarouselLogoRemove = () => setCarouselLogo(null);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (savedSelectionRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
    }
  };

  const insertEmojiAtCursor = (emoji) => {
    editorRef.current?.focus();
    restoreSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(emoji);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
    saveSelection();
    setTipsText(editorRef.current.innerHTML);
    onTextChange?.(editorRef.current.innerHTML);
    setTimeout(() => {
      applySegmentBorders(editorRef.current, postSegments);
    }, 50);
  };

  const handleFontStyleChange = (e) => {
    const value = e.target.value;
    applyStyle(value);
    setFontStyle("Font Styles");
  };

  const applyStyle = (style) => {
    editorRef.current?.focus();
    switch (style) {
      case "bold": document.execCommand("bold"); break;
      case "italic": document.execCommand("italic"); break;
      case "bold-italic":
        document.execCommand("bold");
        document.execCommand("italic");
        break;
      case "normal": document.execCommand("removeFormat"); break;
      default: break;
    }
  };

  const canPreview =
    tipsText?.trim()?.length > 0 || previewImages?.length > 0 || showGeneratedImageUI;

  const mediaReady =
    contentType === "video"
      ? videoUpload.ready // CHANGED: a new upload that is done, or a saved video kept/removed when editing
      : contentType === "document"
        ? !!(documentFile || documentUrl)
        : contentType === "carousel"
          ? carouselSlides.length > 0
          : generatedImages.length > 0 || (previewImages?.length ?? 0) > 0;

  const scrollImageSectionIntoView = (token) => {
    if (!imageSectionRef.current) return;
    if (lastScrolledTokenRef.current === token) return;
    let behavior = "smooth";
    if (typeof window !== "undefined" && window.matchMedia) {
      try {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) behavior = "auto";
      } catch {}
    }
    imageSectionRef.current.scrollIntoView({ behavior, block: "center", inline: "nearest" });
    lastScrolledTokenRef.current = token;
  };

  const requestScrollWhenReady = (token) => {
    if (isInsightsLoading) { pendingScrollTokenRef.current = token; return; }
    scrollImageSectionIntoView(token);
  };

  useEffect(() => {
    if (!isInsightsLoading && pendingScrollTokenRef.current) {
      const token = pendingScrollTokenRef.current;
      pendingScrollTokenRef.current = null;
      scrollImageSectionIntoView(token);
    }
  }, [isInsightsLoading]);

  const handleUploadedImagesReady = () => {
    if (!imageUrl) return;
    requestScrollWhenReady(`uploaded:${imageUrl}`);
  };

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const tooltipId = "sources-tooltip";

  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    function handleEsc(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleCopyText = async () => {
    if (!tipsText || !editorRef.current) return;
    try {
      const clone = editorRef.current.cloneNode(true);
      clone.querySelectorAll("[data-divider]").forEach((el) => el.remove());
      const linkedInText = convertHtmlToLinkedIn(clone.innerHTML);
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([linkedInText], { type: "text/plain" }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  // ================= HASHTAG LINE DETECTION =================
  const isHashtagLine = (text) => /#[\w\u0080-\uFFFF]+/.test(text);

  // ================= UNICODE BOLD → PLAIN ASCII =================
  const MATH_BLOCKS = [
    0x1d400, 0x1d434, 0x1d468, 0x1d49c, 0x1d4d0, 0x1d504, 0x1d538,
    0x1d56c, 0x1d5a0, 0x1d5d4, 0x1d608, 0x1d63c, 0x1d670,
  ];

  const foldMath = (str) =>
    (str || "").replace(/[\u{1D400}-\u{1D7FF}]/gu, (ch) => {
      const cp = ch.codePointAt(0);
      for (const start of MATH_BLOCKS) {
        const d = cp - start;
        if (d >= 0 && d < 26) return String.fromCharCode(65 + d);
        if (d >= 26 && d < 52) return String.fromCharCode(97 + d - 26);
      }
      return ch;
    });

  const normalize = (str) =>
    foldMath(str).replace(/\s+/g, " ").trim().toLowerCase();

  const SEGMENT_CLASSES = {
    hook: "border-Brand-neon-600",
    cuerpo: "border-Brand-light-blue-700",
    cta: "border-Brand-red-600",
    hashtags: "border-Brand-yellow-500",
  };

  // ================= APPLY SEGMENT BORDERS =================
  const applySegmentBorders = (editorEl, segments) => {
    if (!editorEl) return;
    const text = editorEl.innerText || "";
    if (!text.trim()) return;

    editorEl.querySelectorAll("[data-divider]").forEach((el) => el.remove());

    const children = Array.from(editorEl.children).filter(
      (child) => !child.hasAttribute("data-divider")
    );
    const markers = segmentMarkersRef.current || segments;
    let hookChildEl = null;

    children.forEach((child, index) => {
      const childText = (child.innerText || child.textContent || "").trim();

      // --- 1. Identity: trust an existing marker, derive it only once ---
      let seg = child.dataset.segment;
      if (!seg) {
        if (!childText) {
          seg = children[index - 1]?.dataset.segment || "cuerpo";
        } else if (isHashtagLine(childText)) {
          seg = "hashtags";
        } else if (
          markers?.hook &&
          normalize(childText).startsWith(normalize(markers.hook).slice(0, 30))
        ) {
          seg = "hook";
        } else if (
          markers?.cta &&
          normalize(childText).startsWith(normalize(markers.cta).slice(0, 30))
        ) {
          seg = "cta";
        } else {
          seg = "cuerpo";
        }
        child.dataset.segment = seg;
      }

      // --- 2. Paint from identity ---
      child.classList.remove(
        "border-l-4", "border-Brand-neon-600", "border-Brand-light-blue-700",
        "border-Brand-red-600", "border-Brand-yellow-500", "pl-3"
      );
      child.classList.add("border-l-4", SEGMENT_CLASSES[seg], "pl-3");
      child.style.marginTop = seg === "hook" && index === 0 ? "" : "4px";

      if (seg === "hook" && !hookChildEl) hookChildEl = child;
    });


    if (hookChildEl) {
      const hookText = hookChildEl.innerText || "";
      const MAX_HOOK = 210;
      if (hookText.length > MAX_HOOK && !isUserEditingRef.current) {
        hookChildEl.innerText = hookText.slice(0, MAX_HOOK);
      }
      const currentCount = Math.min(hookText.length, MAX_HOOK);
      const divider = document.createElement("div");
      divider.setAttribute("data-divider", "true");
      divider.setAttribute("contenteditable", "false");
      divider.style.cssText = "pointer-events: none; position: relative; line-height: 0; margin: 8px 0 4px 0;";
      divider.innerHTML = `
        <div style="border-top: 1px dashed #b0b0b0;"></div>
        <div style="
          position: absolute;
          right: 0;
          top: -8px;
          background: #2e2e2e;
          color: #ffffff;
          font-size: 10px;
          font-weight: bold;
          padding: 2px 6px;
          border-radius: 4px;
          line-height: normal;
        ">${currentCount}/${MAX_HOOK}</div>
      `;
      hookChildEl.insertAdjacentElement("afterend", divider);
    }
  };

  return (
    <section className="bg-white rounded-tr-none md:rounded-tr-2xl rounded-tl-none rounded-br-2xl p-4 shadow-sm relative">
      <div className="relative">
        <div className="flex items-center justify-between border-b pb-2 mb-4">
          <h2 className="text-lg font-bold uppercase tracking-wide text-black-800">
            {t("insights.title")}
          </h2>
          <div ref={wrapperRef} className="relative flex items-center">
            <button
              type="button"
              aria-label="Sources info"
              aria-describedby={open ? tooltipId : undefined}
              aria-expanded={open}
              aria-haspopup="true"
              className="p-2"
              onClick={() => setOpen((v) => !v)}
              onFocus={() => { if (!isTouchDevice) setOpen(true); }}
              onBlur={() => { if (!isTouchDevice) setOpen(false); }}
              onMouseEnter={() => { if (!isTouchDevice) setOpen(true); }}
              onMouseLeave={() => { if (!isTouchDevice) setOpen(false); }}
            >
              <img src={info} alt="" className="w-6 h-6" />
            </button>
            {open && (
              <div
                id={tooltipId}
                role="tooltip"
                className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-[240px] md:w-[390px] bg-gray-800 text-white text-sm rounded-lg shadow-xl p-2 z-50"
              >
                <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-800 rotate-45" />
                {tooltipText}
              </div>
            )}
          </div>
        </div>

        {/* ===== MOBILE BACK — same place as in the left column ===== */}
        {(mobileViewStep === 3 || mobileViewStep === 7) && (
          <div className="pb-4 flex">
            <Button variant="ghost" onClick={onBack} className="w-auto u-btn-sm">
              <img src={arrowLeft} alt="" /> {t("buttons.back")}
            </Button>
          </div>
        )}

        {/* ===== IMAGE SECTION — desktop only, shown after textConfirmed ===== */}
        {!isMobile && textConfirmed && !(contentType === "document" && !isEditMode && !documentFile && !documentUrl && !documentFilename) && (
          <div className="mb-4 border-b">
          {contentType === "carousel" ? (
            <CarouselSection
              slides={carouselSlides}
              onSlidesChange={onSlidesChange}
              onLogoUploadRequest={handleLogoUploadRequest}
              onExportReady={onCarouselExportReady}
              onPreviewClick={onPreviewClick}
              onDownload={onDownload}
              logoBitmap={carouselLogo}
              logoRel={carouselLogoRel}
              onLogoChange={setCarouselLogoRel}
              onLogoRemove={handleCarouselLogoRemove}
              userEmail={userEmail}
              onOpenAccount={onOpenAccount}
              onPreviewStateChange={onCarouselPreviewState}
            />
          ) : contentType === "video" ? (
            <VideoSection
              onRemoved={onVideoRemoved}
              onPreviewClick={onPreviewClick}
              onPick={onPickVideo}
              isEditMode={isEditMode}
            />
          ) : contentType === "document" ? (
            <DocumentMedia
              file={documentFile}
              url={documentUrl}
              filename={documentFilename}
              onFileSelect={onDocumentSelect}
              onDelete={onDocumentDelete}
              onPreviewClick={onPreviewClick}
              onDownload={onDownload}
            />
          ) : (
            <ImageSection
              imageFile={previewImages?.[0] instanceof File ? previewImages[0] : null}
              selectedPostId={selectedPostId}
              imageLocked={imageLocked}
              isInsightsLoading={isInsightsLoading}
              mobileViewStep={mobileViewStep}
              imageMode={imageMode}
              setImageMode={setImageMode}
              generatingImage={generatingImage}
              imageError={imageError}
              generatedImages={generatedImages}
              selectedImageIndex={selectedImageIndex}
              onSelectImage={onSelectImage}
              generatedImagesCount={generatedImagesCount}
              maxGeneratedImages={maxGeneratedImages}
              showGeneratedImageUI={showGeneratedImageUI}
              imageSectionRef={imageSectionRef}
              onRegenerateImage={onRegenerateImage}
              onDeleteImage={onDeleteImage}
              imgCopied={imgCopied}
              setImgCopied={setImgCopied}
              imageUrl={imageUrl}
              handleUploadedImagesReady={handleUploadedImagesReady}
              isEditMode={isEditMode}
              onLogoApplied={onLogoApplied}
              onPreviewClick={onPreviewClick}
              onDownload={onDownload}
              userEmail={userEmail}
              onOpenAccount={onOpenAccount}
              preLogoImage={preLogoImage}
            />
          )}
          </div>
        )}

        {/* ===== EDITOR ===== */}
        {mobileViewStep !== 7 && (
          <div id="section-editor-text-generated" className={`border rounded-lg bg-gray-50 h-auto md:h-[294px] relative ${hideTextForMedia ? "hidden" : ""}`}>
            <div className="flex flex-wrap items-center gap-2 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg w-full relative z-20">
              <ToolbarTooltipButton
                label={t("buttons.undo")}
                onClick={handleUndo}
                disabled={history.length === 0}
              >
                <img src={undo} alt="" className="w-4 h-4" />
              </ToolbarTooltipButton>
              <ToolbarTooltipButton
                label={t("buttons.redo")}
                onClick={handleRedo}
                disabled={redoStack.length === 0}
              >
                <img src={redo} alt="" className="w-4 h-4" />
              </ToolbarTooltipButton>
              <ToolbarTooltipButton
                label={t("buttons.fontStyle")}
                wrapperClassName="relative inline-flex mt-2 flex-1 min-w-0"
                className="flex-1 min-w-0 w-full"
                noButton={true}
              >
                <select
                  value={fontStyle}
                  onChange={handleFontStyleChange}
                  disabled={!editorHasText}
                  className="h-10 sm:h-11 w-full px-2 border border-black-600 rounded bg-white
                    text-gray-500 text-sm cursor-pointer
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option>{t("insights.fontStyle")}</option>
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                  <option value="italic">Italic</option>
                  <option value="bold-italic">Bold Italic</option>
                </select>
              </ToolbarTooltipButton>
              <div className="relative">
                <ToolbarTooltipButton
                  label={t("buttons.emojis")}
                  onClick={() => setIsSmileDropdownOpen((prev) => !prev)}
                  disabled={!editorHasText}
                >
                  {selectedEmoji ? selectedEmoji : <img src={smile} alt="" className="w-5 h-5" />}
                </ToolbarTooltipButton>
                {isSmileDropdownOpen && (
                  <div className="absolute top-14 right-0 z-50">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        insertEmojiAtCursor(emojiData.emoji);
                        setSelectedEmoji(emojiData.emoji);
                        setIsSmileDropdownOpen(false);
                      }}
                      height={350}
                      width={300}
                    />
                  </div>
                )}
              </div>
              <ToolbarTooltipButton
                label={t("insights.preview")}
                onClick={onPreviewClick}
                disabled={!canPreview}
              >
                <img src={eye} alt="preview" className="w-4 h-4" />
              </ToolbarTooltipButton>
            </div>

            <div className={`p-2 ${tipsText ? "h-auto" : "h-full"} relative bg-black-0 rounded-br-lg rounded-bl-lg`}>
              {!tipsText && (
                <div className="relative inset-0 flex flex-col justify-start space-y-3 overflow-hidden pointer-events-none">
                  {["max-w-[400px]", "max-w-[500px]", "max-w-[440px]", "max-w-[500px]", "max-w-[260px]"].map((width, i) => (
                    <div className="flex space-x-2" key={i}>
                      <div
                        className={`h-6 u-skeleton w-full ${width}`}
                        style={{ "--sk-delay": `${i * 0.18}s` }}
                      ></div>
                    </div>
                  ))}
                </div>
              )}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className={`w-full h-full min-h-[200px] bg-transparent focus:outline-none text-gray-700 relative z-10 caret-gray-700 px-2 leading-normal overflow-y-auto break-words text-xl ${
                  !tipsText ? "hidden" : ""
                }`}
                style={{ whiteSpace: "pre-wrap" }}
                onFocus={() => { isUserEditingRef.current = true; }}
                onBlur={() => {
                  isUserEditingRef.current = false;
                  clearTimeout(applyBordersTimerRef.current);
                  applyBordersTimerRef.current = setTimeout(() => {
                    applySegmentBorders(editorRef.current, postSegments);
                  }, 300);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;

                  const sel = window.getSelection();
                  if (!sel || sel.rangeCount === 0) return;

                  const node = sel.anchorNode;
                  const el = node?.nodeType === 1 ? node : node?.parentElement;
                  if (!el?.closest?.('[data-segment="hook"]')) return; // not in hook → normal Enter

                  e.preventDefault(); // never split the hook

                  const bodyDiv = editorRef.current?.querySelector('[data-segment="cuerpo"]');
                  if (bodyDiv) {
                    const range = document.createRange();
                    range.selectNodeContents(bodyDiv);
                    range.collapse(true); // caret at start of body
                    sel.removeAllRanges();
                    sel.addRange(range);
                    saveSelection();
                  } else if (autoSplit) {
                    // Own text with nothing after the hook yet: start a body line
                    const hookEl = el.closest('[data-segment="hook"]');
                    const next = hookEl.nextElementSibling;
                    const after = next?.hasAttribute("data-divider") ? next : hookEl;
                    const line = document.createElement("div");
                    line.dataset.segment = "cuerpo";
                    line.innerHTML = "<br>";
                    after.insertAdjacentElement("afterend", line);
                    const range = document.createRange();
                    range.selectNodeContents(line);
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);
                    saveSelection();
                  }
                }}
                onInput={() => {
                  const hookEl = editorRef.current?.children[0];
                  if (hookEl) {
                    const hookText = hookEl.innerText || "";
                    if (hookText.length > 210) {
                      const selection = window.getSelection();
                      hookEl.innerText = hookText.slice(0, 210);
                      const range = document.createRange();
                      range.selectNodeContents(hookEl);
                      range.collapse(false);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                    const currentCount = Math.min(hookEl.innerText.length, 210);
                    const dividerEl = editorRef.current?.querySelector("[data-divider]");
                    if (dividerEl) {
                      const counterEl = dividerEl.querySelector("div:last-child");
                      if (counterEl) {
                        counterEl.textContent = `${currentCount}/210`;
                      }
                    }
                  }
                  setHistory((prev) => [...prev.slice(-49), editorRef.current.innerHTML]);
                  setRedoStack([]);
                  saveSelection();
                  setTipsText(editorRef.current.innerHTML);
                  onTextChange?.(editorRef.current.innerHTML);
                  // Re-extract segments from editor after each edit
                  if (postSegments && onSegmentsChange) {
                    const children = Array.from(editorRef.current.children)
                      .filter((el) => !el.hasAttribute("data-divider"));

                    const bucket = { hook: [], cuerpo: [], cta: [], hashtags: [] };
                    children.forEach((el) => {
                      const seg = el.dataset.segment || "cuerpo";
                      if (!bucket[seg]) return;
                      bucket[seg].push(convertHtmlToLinkedIn(el.innerHTML));
                    });

                    const clean = (arr) => {
                      const a = [...arr];
                      while (a.length && !a[0].trim()) a.shift();
                      while (a.length && !a[a.length - 1].trim()) a.pop();
                      return a.join("\n");
                    };

                    onSegmentsChange({
                      hook: bucket.hook.length ? clean(bucket.hook) : postSegments.hook,
                      cuerpo: clean(bucket.cuerpo),
                      cta: bucket.cta.length ? clean(bucket.cta) : postSegments.cta,
                      hashtags:
                        bucket.hashtags.join(" ").match(/#[\w\u0080-\uFFFF]+/g)?.map((h) => h.slice(1)) ||
                        postSegments.hashtags,
                    });
                  }
                  // Own text: re-split by the rules and re-mark every line
                  if (autoSplit) {
                    const lineEls = Array.from(editorRef.current.children)
                      .filter((el) => !el.hasAttribute("data-divider"));
                    const { segments, roles } = splitEditorLines( 
                      lineEls.map((el) => convertHtmlToLinkedIn(el.innerHTML))
                    );
                    lineEls.forEach((el, i) => {
                      el.dataset.segment = roles[i];
                    });
                    onSegmentsChange?.(segments);
                  }
                  clearTimeout(applyBordersTimerRef.current);
                  applyBordersTimerRef.current = setTimeout(() => {
                    applySegmentBorders(editorRef.current, postSegments);
                  }, 500);
                }}
                onKeyUp={(e) => {
                  saveSelection();
                  if (e.key === "Enter") {
                    clearTimeout(applyBordersTimerRef.current);
                    applyBordersTimerRef.current = setTimeout(() => {
                      applySegmentBorders(editorRef.current, postSegments);
                    }, 50);
                  }
                }}
                onMouseUp={saveSelection}
              />
              <div className="flex justify-end">
                <ToolbarTooltipButton
                  label={t("buttons.copy")}
                  onClick={handleCopyText}
                  disabled={!editorHasText}
                >
                  <img src={copyIcon} alt="" className="w-4 h-4" />
                </ToolbarTooltipButton>
              </div>
            </div>
          </div>
        )}

        {/* ===== CHARACTER COUNT ===== */}
        {mobileViewStep !== 7 && (
          <div className={`flex flex-col items-end mt-1 mb-2 ${hideTextForMedia ? "hidden" : ""}`}>
            <span className="text-sm font-bold">{tipsText.length}/{MAX_CHARACTERS}</span>
          </div>
        )}

        {/* ===== SEGMENT LEGEND ===== */}
        {mobileViewStep !== 7 && (
          <div className={`space-y-2 mb-4 text-sm text-gray-600 ${hideTextForMedia ? "hidden" : ""}`}>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-Brand-neon-600"></span>
              <span>{t("tips.hook")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-Brand-light-blue-700"></span>
              <span>{t("tips.body")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-Brand-red-600"></span>
              <span>{t("tips.cta")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-Brand-yellow-500"></span>
              <span>{t("tips.hashtag")}</span>
            </div>
          </div>
        )}

        {/* ===== MOBILE MEDIA BRANCH ===== */}
        {mobileViewStep === 7 && (
          contentType === "carousel" ? (
            <CarouselSection
              slides={carouselSlides}
              onSlidesChange={onSlidesChange}
              mobileViewStep={mobileViewStep}
              onLogoUploadRequest={handleLogoUploadRequest}
              onExportReady={onCarouselExportReady}
              onPreviewClick={onPreviewClick}
              onDownload={onDownload}
              logoBitmap={carouselLogo}
              logoRel={carouselLogoRel}
              onLogoChange={setCarouselLogoRel}
              onLogoRemove={handleCarouselLogoRemove}
              userEmail={userEmail}
              onOpenAccount={onOpenAccount}
              onPreviewStateChange={onCarouselPreviewState}
            />
          ) : contentType === "video" ? (
            <VideoSection
              onRemoved={onVideoRemoved}
              onPreviewClick={onPreviewClick}
              onPick={onPickVideo}
              isEditMode={isEditMode}
            />
          ) : contentType === "document" ? (
            <DocumentMedia
              file={documentFile}
              url={documentUrl}
              filename={documentFilename}
              onFileSelect={onDocumentSelect}
              onDelete={onDocumentDelete}
              onPreviewClick={onPreviewClick}
              onDownload={onDownload}
            />
          ) : (
            <ImageSection
              imageFile={previewImages?.[0] instanceof File ? previewImages[0] : null}
              selectedPostId={selectedPostId}
              imageLocked={imageLocked}
              isInsightsLoading={isInsightsLoading}
              mobileViewStep={mobileViewStep}
              imageMode={imageMode}
              setImageMode={setImageMode}
              generatingImage={generatingImage}
              imageError={imageError}
              generatedImages={generatedImages}
              selectedImageIndex={selectedImageIndex}
              onSelectImage={onSelectImage}
              generatedImagesCount={generatedImagesCount}
              maxGeneratedImages={maxGeneratedImages}
              showGeneratedImageUI={showGeneratedImageUI}
              imageSectionRef={imageSectionRef}
              onRegenerateImage={onRegenerateImage}
              onDeleteImage={onDeleteImage}
              imgCopied={imgCopied}
              setImgCopied={setImgCopied}
              imageUrl={imageUrl}
              handleUploadedImagesReady={handleUploadedImagesReady}
              isEditMode={isEditMode}
              onLogoApplied={onLogoApplied}
              onPreviewClick={onPreviewClick}
              onDownload={onDownload}
              userEmail={userEmail}
              onOpenAccount={onOpenAccount}
              preLogoImage={preLogoImage}
            />
          )
        )}

        {mobileViewStep === 7 && (
          <div className="mt-4 flex flex-col gap-3">
            <Button variant="dark" className="w-full" disabled={!mediaReady} onClick={onConfirm}>
              {t(
                contentType === "carousel"
                  ? "buttons.confirmCarousel"
                  : contentType === "video"
                    ? "buttons.confirmVideo"
                    : contentType === "document"
                      ? "buttons.confirmDocument"
                      : "buttons.confirmImage"
              )} <img src={arrowRight} alt="" />
            </Button>
          </div>
        )}

        {/* ===== IMAGE SUGGESTIONS — always visible, controls itself ===== */}
        <ImageSuggestions
          mobileViewStep={mobileViewStep}
          showGeneratedImageUI={showGeneratedImageUI}
          imageUrl={imageUrl}
          imageLocked={imageLocked}
        />

        {/* ===== FIRST COMMENT ===== */}
        {(mobileViewStep === 7 || !isMobile) && imageLocked && (
          <FirstComment canIncludeFirstComment={canIncludeFirstComment} tipsText={tipsText} />
        )}

        {/* ===== MOBILE STEP 3 BUTTONS ===== */}
        {mobileViewStep === 3 && (
          <div className="mt-4 flex flex-col gap-3">
            <Button variant="dark" className="w-full" onClick={onConfirm}>
              {t("buttons.confirmText")} <img src={arrowRight} alt="" />
            </Button>
          </div>
        )}        

        {copied && (
          <div className="fixed bottom-6 right-6 bg-black-800 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in">
            {t("copyToClipboard.text")}
          </div>
        )}
        {imgCopied && (
          <div className="fixed bottom-6 right-6 bg-black-800 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in">
            {t("copyToClipboard.image")}
          </div>
        )}
      </div>
      <LogoPickerModal
        open={showLogoPicker}
        onClose={() => setShowLogoPicker(false)}
        logos={savedLogos}
        onSelect={handleLogoFromBase64}
        onOpenAccount={onOpenAccount}
        error={logoPickError}
      />
    </section>
  );
}